
import { coreAiService } from "./coreService";
import { AiResponse } from "./types";

// Editorial service for fact checking, content optimization, and SEO analysis
export const editorialService = {
  async factCheck(content: string): Promise<AiResponse> {
    const systemPrompt = `You are a meticulous fact-checker for journalistic content.
    Analyze the article for factual claims, statistics, dates, names, and quotes.
    Identify potentially incorrect information, claims that need citation, and inconsistencies.
    For each issue found, provide specific feedback and suggestions for verification.
    Format as JSON with an array of issues, each containing 'text', 'issue', 'severity' (high/medium/low) and 'suggestion'.
    If no issues are found, return an empty array.`;

    const prompt = `Fact check the following article content and identify any potential issues: 
    ${content.substring(0, 4000)}${content.length > 4000 ? '...' : ''}`;

    return coreAiService.generateContent({
      systemPrompt,
      prompt,
      temperature: 0.3,
      maxTokens: 1500,
      model: 'gpt-4o-mini',
      content
    });
  },

  async optimizeWriting(content: string, style: 'concise' | 'expanded' | 'formal' | 'conversational' = 'concise'): Promise<AiResponse> {
    const styleGuide = {
      concise: "Focus on brevity and clarity. Remove unnecessary words and simplify complex sentences.",
      expanded: "Elaborate on key points with supporting details while maintaining clarity.",
      formal: "Use professional language, avoid contractions, and maintain an authoritative tone.",
      conversational: "Write in a friendly, approachable tone using contractions and relatable language."
    };

    const systemPrompt = `You are an expert editor specializing in journalistic writing.
    Optimize the provided text for grammar, clarity, and style while preserving the author's voice and intent.
    Apply the following style guidelines: ${styleGuide[style]}.
    Format as JSON with 'original' and 'optimized' text segments, and include a 'rationale' for significant changes.`;

    const prompt = `Optimize this text for ${style} style while fixing any grammar or clarity issues: 
    ${content.substring(0, 3000)}${content.length > 3000 ? '...' : ''}`;

    return coreAiService.generateContent({
      systemPrompt,
      prompt,
      temperature: 0.4,
      maxTokens: 3500,
      model: 'gpt-4o-mini',
      content
    });
  },

  async analyzeSeo(content: string, title: string): Promise<AiResponse> {
    const systemPrompt = `You are an SEO specialist for a digital publication.
    Analyze the article title and content for SEO effectiveness and provide actionable suggestions.
    Consider keyword usage, title length, meta description, headers, content length, and readability.
    Assign an overall SEO score (0-100) and provide specific recommendations for improvement.
    Format as JSON with 'score', 'analysis' and 'recommendations' properties, with 'recommendations' being an array of specific suggestions.`;

    const prompt = `Analyze this article for SEO effectiveness:
    Title: ${title}
    Content: ${content.substring(0, 3000)}${content.length > 3000 ? '...' : ''}`;

    return coreAiService.generateContent({
      systemPrompt,
      prompt,
      temperature: 0.3,
      maxTokens: 1500,
      model: 'gpt-4o-mini',
      content,
      title
    });
  }
};
